import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { fetchProfileAndSyncUser } from '../profile/api';
import { shouldRetryPreferencesQuery } from '../../../platform/support/issueReport';
import { resolveCurrentApiPanel } from '../../query/normalizers/resolveApiPanel';
import { resolveApiPanelCapabilities } from '../../query/normalizers/capabilities';
import { keys } from '../../query/keyRegistry';
import { getAllergyPickerMode } from '../config/appConfig';
import {
  fetchAllergyDislikes,
  fetchAllergyDislikeCategories,
  saveAllergyDislikes,
  type AllergyDislikesResponse,
} from './allergyDislikesApi';
import {
  fetchAllergies,
  fetchDislikes,
} from './api';
import {
  buildInventoryTypeMap,
  extractCategoriesFromAllergyDislikeResponse,
  extractSelectedInventoryIdsFromResponse,
  inventoryIdsFromProfile,
  splitInventoryIdsByType,
  type InventoryPreferenceType,
} from './inventoryPreferenceTypes';
import { useMemo } from 'react';

export const ALLERGY_DISLIKES_QUERY_KEY = ['preferences', 'allergy_dislikes'] as const;
export const ALLERGY_DISLIKE_CATEGORIES_QUERY_KEY = ['preferences', 'allergy_dislike_categories'] as const;
export const PROFILE_ALLERGIES_DISLIKES_QUERY_KEY = ['profile', 'allergies-dislikes'] as const;

/** Combined catalog cache TTL — reopen/save forces refresh regardless. */
export const COMBINED_PREFERENCES_STALE_MS = 1000 * 60 * 10;

const preferences_query_retry = (failure_count: number, error: unknown) =>
  shouldRetryPreferencesQuery(failure_count, error);

function shouldUseCombinedAllergyApi(): boolean {
  if (getAllergyPickerMode() === 'combined') {
    return true;
  }
  const panel = resolveCurrentApiPanel();
  return resolveApiPanelCapabilities(panel).combined_allergy_api;
}

async function fetchPreferencesCatalog(customer_id: number): Promise<AllergyDislikesResponse> {
  if (shouldUseCombinedAllergyApi()) {
    return fetchAllergyDislikes(customer_id);
  }

  // v1 split APIs: `allergies` + `dislikes` (active screen uses legacyAllergiesHooks directly).
  const [allergies_res, dislikes_res] = await Promise.all([
    fetchAllergies(customer_id),
    fetchDislikes(customer_id),
  ]);

  const allergy_ids =
    allergies_res?.data?.customer_selected_allergy_ids ??
    allergies_res?.data?.customer_selected_allergies ??
    allergies_res?.customer_selected_allergy_ids ??
    [];
  const dislike_ids =
    dislikes_res?.data?.customer_selected_dislike_ids ??
    dislikes_res?.data?.customer_selected_dislikes ??
    dislikes_res?.customer_selected_dislike_ids ??
    [];

  return {
    data: {
      categories: [
        ...(allergies_res?.data?.categories ?? allergies_res?.categories ?? []),
        ...(dislikes_res?.data?.categories ?? dislikes_res?.categories ?? []),
      ] as NonNullable<AllergyDislikesResponse['data']>['categories'],
      customer_selected_items: [...allergy_ids, ...dislike_ids],
    },
  };
}

/** Prefetch combined `allergy_dislikes` at launch (10m staleTime). */
export async function prefetchCombinedPreferencesCatalog(
  query_client: QueryClient,
  customer_id: number,
): Promise<void> {
  if (getAllergyPickerMode() !== 'combined') {
    return;
  }
  await query_client.prefetchQuery({
    queryKey: keys.customer(customer_id).preferences,
    queryFn: () => fetchPreferencesCatalog(customer_id),
    staleTime: COMBINED_PREFERENCES_STALE_MS,
  });
}

/** Force refresh combined catalog (allergies page open / after save). */
export async function refetchCombinedPreferencesCatalog(
  query_client: QueryClient,
  customer_id: number,
): Promise<void> {
  await query_client.invalidateQueries({ queryKey: keys.customer(customer_id).preferences });
  await query_client.invalidateQueries({ queryKey: ALLERGY_DISLIKES_QUERY_KEY });
  await query_client.fetchQuery({
    queryKey: keys.customer(customer_id).preferences,
    queryFn: () => fetchPreferencesCatalog(customer_id),
    staleTime: COMBINED_PREFERENCES_STALE_MS,
  });
}

export function usePreferencesCatalog(customer_id: number | undefined) {
  const combined = getAllergyPickerMode() === 'combined' || shouldUseCombinedAllergyApi();
  return useQuery({
    queryKey: keys.customer(customer_id ?? 0).preferences,
    queryFn: () => fetchPreferencesCatalog(customer_id!),
    enabled: typeof customer_id === 'number',
    retry: preferences_query_retry,
    staleTime: combined ? COMBINED_PREFERENCES_STALE_MS : undefined,
  });
}

export function useAllergyDislikeCategories(customer_id: number | undefined) {
  return useQuery({
    queryKey: [...ALLERGY_DISLIKE_CATEGORIES_QUERY_KEY, customer_id],
    queryFn: () => fetchAllergyDislikeCategories(customer_id!),
    enabled: typeof customer_id === 'number',
    retry: preferences_query_retry,
    staleTime: 1000 * 60 * 5,
  });
}

export function useSavePreferencesMutation() {
  const query_client = useQueryClient();
  const user = useAuthStore(s => s.user);
  const customer_id = user?.id;

  // v3 only — POST `allergy_dislikes`. v1 screen uses useSaveAllergiesMutation / useSaveDislikesMutation.
  return useMutation({
    mutationFn: (inventory_ids: number[]) => saveAllergyDislikes(customer_id!, inventory_ids),
    onSuccess: async () => {
      if (typeof customer_id === 'number') {
        await refetchCombinedPreferencesCatalog(query_client, customer_id);
      }
      query_client.invalidateQueries({ queryKey: PROFILE_ALLERGIES_DISLIKES_QUERY_KEY });
      query_client.invalidateQueries({ queryKey: ['profile'] });
      if (typeof customer_id === 'number') {
        await fetchProfileAndSyncUser(customer_id).catch(() => {});
      }
    },
  });
}

export function useSelectedInventoryIds(): number[] {
  const user = useAuthStore(s => s.user);
  const customer_id = user?.id;
  const { data, isSuccess } = usePreferencesCatalog(customer_id);

  return useMemo(() => {
    if (isSuccess && typeof customer_id === 'number') {
      const from_api = extractSelectedInventoryIdsFromResponse(data as AllergyDislikesResponse);
      if (from_api !== null) {
        return from_api;
      }
      // Unknown response shape — keep menu health working from profile.
      return inventoryIdsFromProfile(user);
    }
    // Catalog not loaded yet — profile inventory / allergy+dislike ids.
    return inventoryIdsFromProfile(user);
  }, [user, isSuccess, customer_id, data, user?.inventory_ids, user?.allergy_ids, user?.dislike_ids]);
}

export function useInventoryTypeMap(): Map<number, InventoryPreferenceType> {
  const customer_id = useAuthStore(s => s.user?.id);
  const { data } = usePreferencesCatalog(customer_id);

  return useMemo(() => {
    const categories = extractCategoriesFromAllergyDislikeResponse(data);
    return buildInventoryTypeMap(categories);
  }, [data]);
}

export function useSelectedAllergyIdsFromInventory(): number[] {
  const inventory_ids = useSelectedInventoryIds();
  const type_map = useInventoryTypeMap();

  return useMemo(
    () => splitInventoryIdsByType(inventory_ids, type_map).allergy_ids,
    [inventory_ids, type_map],
  );
}

export function useSelectedDislikeIdsFromInventory(): number[] {
  const inventory_ids = useSelectedInventoryIds();
  const type_map = useInventoryTypeMap();

  return useMemo(
    () => splitInventoryIdsByType(inventory_ids, type_map).dislike_ids,
    [inventory_ids, type_map],
  );
}
